import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Link } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { formatCurrency } from 'src/utils/numberFormatter';
interface PayoutSectionLineItem {
  id: string;
  label: string;
  amount: number;
  showSign?: boolean;
}

interface PayoutSectionTotal {
  label: string;
  amount: number;
  showSign?: boolean;
}

interface PayoutSectionProps {
  title: string;
  lineItems: PayoutSectionLineItem[];
  total?: PayoutSectionTotal;
  emptyMessage?: string;
  feesSection?: boolean;
  payoutId?: string;
}

interface SectionLineProps {
  label: string;
  amount: number;
  isSubtotal?: boolean;
  isTotal?: boolean;
  showSign?: boolean;
}

function SectionLine({
  label,
  amount,
  isSubtotal = false,
  isTotal = false,
  showSign = false,
}: SectionLineProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography
        variant={isTotal ? 'h6' : 'body1'}
        fontWeight={isSubtotal || isTotal ? 600 : 400}
      >
        {label}
      </Typography>
      <Typography
        variant={isTotal ? 'h6' : 'body1'}
        fontWeight={isSubtotal || isTotal ? 600 : 400}
        color={
          amount < 0
            ? 'error.main'
            : showSign && amount > 0
            ? 'success.main'
            : 'inherit'
        }
      >
        {amount < 0 ? '-' : showSign && amount > 0 ? '+' : ''}
        {formatCurrency(Math.abs(amount))}
      </Typography>
    </Stack>
  );
}

export function PayoutSection({
  title,
  lineItems,
  total,
  emptyMessage,
  feesSection,
  payoutId,
}: PayoutSectionProps) {
  const hasItems = lineItems.length > 0;
  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h6">
          {title}
        </Typography>
        {feesSection && (
          <IconButton
            color="primary"
            component={Link}
            to={`/franchise-fees/activity/?payoutId=${payoutId}`}
          >
            <Icon icon={icon({ name: 'link', style: 'regular' })} size="small" />
          </IconButton>
        )}
      </Stack>


      {!hasItems && emptyMessage ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyMessage}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          <Stack spacing={0.5}>
            {lineItems.map((item) => (
              <SectionLine
                key={item.id}
                label={item.label}
                amount={item.amount}
                showSign={item.showSign}
              />
            ))}
          </Stack>

          {total && (
            <>
              {/* Divider line */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 1 }} />
              <SectionLine
                label={total.label}
                amount={total.amount}
                showSign={total.showSign}
                isSubtotal
              />
            </>
          )}
        </Stack>
      )}
    </Box>
  );
}
